import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Modal,
  Fade,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Autocomplete,
  Avatar,
  Stack,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  alpha,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PaidIcon from '@mui/icons-material/Paid';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SearchIcon from '@mui/icons-material/Search';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import VerifiedIcon from '@mui/icons-material/Verified';
import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { fmtMoney } from './PendingItemsModal';
import { DevWinBadge, DevTabBadge } from '../DevPageBadge';
import ExpensesDetail from '../../views/Finance/ExpensesDetail';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '96vw',
  maxWidth: 760,
  maxHeight: '92vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 3,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

// ป้ายเลขขั้นตอนในฟอร์มเพิ่มรายการ (1 ผูกรายการเบิก / 2 รายละเอียดเงินคืน)
const addStepBadgeSx = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  bgcolor: '#0288d1',
  color: '#fff',
  fontSize: '0.75rem',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const expenseLabel = (exp) => [exp?.code, exp?.name].filter(Boolean).join(' · ') || '-';

// สีวันที่ตั้งเบิกในตัวเลือกรายการเบิก — วันเดียวกัน = สีเดียวกัน วนใช้ 12 สีหลักตามลำดับวันที่
// (เหลืองใช้โทนเข้มขึ้นให้อ่านออกบนพื้นขาว, ขาวมีเส้นขอบตัวอักษรสีเข้มไม่งั้นมองไม่เห็น)
const EXPENSE_DATE_GROUP_COLORS = [
  { color: '#dc2626' }, // แดง
  { color: '#ea580c' }, // ส้ม
  { color: '#ca8a04' }, // เหลือง
  { color: '#16a34a' }, // เขียว
  { color: '#1d4ed8' }, // น้ำเงิน
  { color: '#0ea5e9' }, // ฟ้า
  { color: '#9333ea' }, // ม่วง
  { color: '#db2777' }, // ชมพู
  { color: '#ffffff', textShadow: '0 0 1px #000, 0 0 1px #000, 0 0 1px #000' }, // ขาว
  { color: '#000000' }, // ดำ
  { color: '#6b7280' }, // เทา
  { color: '#92400e' }, // น้ำตาล
];
const expenseDateKey = (exp) => (exp?.date ? dayjs(exp.date).format('YYYY-MM-DD') : 'no-date');

export const REFUND_PENDING_COLOR = '#0288d1';
export const REFUND_RECEIVED_COLOR = '#2e7d32';

// รายการเงินมัดจำ/ค่าประกันที่รอรับคืนของ budget เดียว — ทำงานคู่ขนานกับ PendingItemsModal (AP Pending)
// ต่างกันตรงที่นี่คือค่ามัดจำที่ติดไปกับ expense (นับเป็นต้นทุนไปแล้ว) แต่จะได้เงินคืนภายหลัง ไม่ใช่ต้นทุนจริงของบริษัท
// expenses: รายการเบิกของ budget นี้ ให้เลือกผูกตอนเพิ่มรายการ — ไม่ส่งมาจะใช้ budget.expenses (มีเฉพาะ budget
// จาก budgetAll ที่ BudgetTable ใช้) อ่านจาก prop ตรงๆ ไม่ใช่ localBudget เพราะ response ของ API refund ไม่มี expenses ติดมา
export default function RefundItemsModal({ open, budget, expenses, onClose, onUpdated }) {
  const dispatch = useDispatch();
  const [localBudget, setLocalBudget] = useState(budget);
  const [refundTab, setRefundTab] = useState(0); // 0 = รอคืน, 1 = คืนแล้ว
  const [refundForm, setRefundForm] = useState({ name: '', amount: '', selectedExpenses: [] });
  const [refundSaving, setRefundSaving] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  // กดเพิ่มไปแล้วอย่างน้อยครั้งเดียว — เริ่มโชว์กรอบแดงช่องที่ยังไม่กรอก (ไม่แดงตั้งแต่เปิด dialog)
  const [addSubmitted, setAddSubmitted] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', amount: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [receiveItem, setReceiveItem] = useState(null);
  const [receiveFile, setReceiveFile] = useState(null);
  const [receiveSaving, setReceiveSaving] = useState(false);
  const [expenseDetailId, setExpenseDetailId] = useState(null);
  // รูปตัวอย่างสลิปใน dialog ยืนยันได้รับเงินคืน — revoke URL เดิมทุกครั้งที่เปลี่ยนไฟล์/ปิด dialog
  const [receivePreviewUrl, setReceivePreviewUrl] = useState(null);
  useEffect(() => {
    if (!receiveFile || !receiveFile.type?.startsWith('image/')) {
      setReceivePreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(receiveFile);
    setReceivePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [receiveFile]);

  // แก้ไขรูปหลักฐานของรายการที่ได้รับคืนแล้ว (แท็บ "คืนแล้ว")
  const [slipEditItem, setSlipEditItem] = useState(null);
  const [slipEditFile, setSlipEditFile] = useState(null);
  const [slipEditSaving, setSlipEditSaving] = useState(false);
  const [slipEditPreviewUrl, setSlipEditPreviewUrl] = useState(null);
  useEffect(() => {
    if (!slipEditFile || !slipEditFile.type?.startsWith('image/')) {
      setSlipEditPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(slipEditFile);
    setSlipEditPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [slipEditFile]);

  useEffect(() => {
    if (open) {
      setLocalBudget(budget);
      setRefundTab(0);
    }
  }, [open, budget]);

  const budgetId = localBudget?.id || localBudget?._id;
  const refundItemsAll = localBudget?.refund_items || [];
  const refundItemsPending = _.filter(refundItemsAll, (i) => !i.refunded);
  const refundItemsReceived = _.filter(refundItemsAll, (i) => i.refunded);
  const refundItemsShown = refundTab === 0 ? refundItemsPending : refundItemsReceived;

  const expenseOptions = _.orderBy(
    _.filter(expenses ?? budget?.expenses ?? [], (e) => !String(e?.status || '').startsWith('REJECT')),
    [(e) => new Date(e?.date || 0)],
    ['desc'],
  );
  // วันที่ตั้งเบิก -> สีกลุ่ม ไล่ตามลำดับที่วันนั้นโผล่ในตัวเลือก (เรียงวันที่ล่าสุดก่อนอยู่แล้ว วันเดียวกันจึงอยู่ติดกัน)
  const dateGroupStyleByKey = new Map();
  expenseOptions.forEach((e) => {
    const key = expenseDateKey(e);
    if (!dateGroupStyleByKey.has(key)) {
      dateGroupStyleByKey.set(key, EXPENSE_DATE_GROUP_COLORS[dateGroupStyleByKey.size % EXPENSE_DATE_GROUP_COLORS.length]);
    }
  });
  const selectedExpensesTotal = _.sumBy(refundForm.selectedExpenses, (e) => Number(e?.price) || 0);
  // เตือน (ไม่บล็อก) ถ้ายอดรอคืนมากกว่ายอดเบิกรวมของรายการที่เลือก
  const addAmountOverExpense = refundForm.selectedExpenses.length > 0
    && parseFloat(refundForm.amount) > selectedExpensesTotal;
  // ใช้แสดงรายการเบิกที่ผูกไว้ในแต่ละแถว — รวมทุกสถานะ (ไม่ตัด REJECT เหมือนตัวเลือกตอนเพิ่ม)
  const expenseById = new Map(
    (expenses ?? budget?.expenses ?? []).map((e) => [String(e?._id || e?.id), e]),
  );

  // ฟอร์มเพิ่มรายการบังคับกรอกครบทุกช่อง (รายการเบิก / ชื่อ / จำนวนเงิน > 0)
  const addAmountNum = parseFloat(refundForm.amount);
  const addErrors = {
    expense: !refundForm.selectedExpenses.length,
    name: !refundForm.name.trim(),
    amount: Number.isNaN(addAmountNum) || addAmountNum <= 0,
  };
  const showAddError = (field) => addSubmitted && addErrors[field];

  // รายการเบิกต้นทางของรายการที่กำลังยืนยันรับคืน (หลายรายการ / รูปแบบเก่ารายการเดียว)
  const receiveLinkedExpenses = (receiveItem?.source_expense_ids?.length
    ? receiveItem.source_expense_ids
    : [receiveItem?.source_expense_id].filter(Boolean)
  ).map((v) => ({ id: String(v), exp: expenseById.get(String(v)) }));

  // refund_items ที่ใบรายรับเงินคืนยืนยัน OCR ในบัญชีบริษัทแล้ว — ยกเลิกการคืน/ลบไม่ได้ (backend ก็กันซ้ำ)
  const [verifiedItemIds, setVerifiedItemIds] = useState(() => new Set());
  const loadVerifiedItemIds = async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`${process.env.REACT_APP_API_URL}/budget/${id}/refund-verified`);
      setVerifiedItemIds(new Set((Array.isArray(data) ? data : []).map(String)));
    } catch (error) {
      setVerifiedItemIds(new Set());
    }
  };
  useEffect(() => {
    if (open) loadVerifiedItemIds(budget?.id || budget?._id);
  }, [open, budget?.id, budget?._id]);

  const applyUpdate = (updated) => {
    setLocalBudget(updated);
    onUpdated?.(updated);
    loadVerifiedItemIds(updated?.id || updated?._id);
  };

  const openAddDialog = () => {
    setRefundForm({ name: '', amount: '', selectedExpenses: [] });
    setAddSubmitted(false);
    setAddDialogOpen(true);
  };

  const closeAddDialog = () => {
    setAddDialogOpen(false);
    setRefundForm({ name: '', amount: '', selectedExpenses: [] });
    setAddSubmitted(false);
  };

  const handleAddRefundItem = async () => {
    setAddSubmitted(true);
    if (addErrors.expense || addErrors.name || addErrors.amount) return;
    const name = refundForm.name.trim();
    const amount = addAmountNum;
    setRefundSaving(true);
    try {
      const source_expense_ids = refundForm.selectedExpenses.map((e) => e?._id || e?.id);
      const updated = await dispatch(actions.budgetAddRefundItem(budgetId, { name, amount, source_expense_ids }));
      applyUpdate(updated);
      closeAddDialog();
      setRefundTab(0);
    } catch (error) {
      showAlert('เพิ่มรายการไม่สำเร็จ');
    } finally {
      setRefundSaving(false);
    }
  };

  const openEditDialog = (item) => {
    setEditItem(item);
    setEditForm({ name: item.name, amount: String(item.amount) });
  };

  const closeEditDialog = () => {
    setEditItem(null);
    setEditForm({ name: '', amount: '' });
  };

  const handleUpdateRefundItem = async () => {
    const name = editForm.name.trim();
    const amount = parseFloat(editForm.amount);
    if (!name || isNaN(amount) || amount <= 0) {
      showAlert('กรุณากรอกชื่อรายการและจำนวนเงินให้ถูกต้อง');
      return;
    }
    setEditSaving(true);
    try {
      const updated = await dispatch(actions.budgetUpdateRefundItem(budgetId, editItem._id, { name, amount }));
      applyUpdate(updated);
      closeEditDialog();
    } catch (error) {
      showAlert('แก้ไขรายการไม่สำเร็จ');
    } finally {
      setEditSaving(false);
    }
  };

  const handleRemoveRefundItem = async (itemId) => {
    if (!(await confirmAsync('ลบรายการนี้?'))) return;
    try {
      const updated = await dispatch(actions.budgetRemoveRefundItem(budgetId, itemId));
      applyUpdate(updated);
    } catch (error) {
      showAlert('ลบรายการไม่สำเร็จ');
    }
  };

  const openReceiveDialog = (item) => {
    setReceiveItem(item);
    setReceiveFile(null);
  };

  const closeReceiveDialog = () => {
    setReceiveItem(null);
    setReceiveFile(null);
  };

  // ยกเลิกการรับคืน — รายการกลับไปแท็บ "รอคืน" (ยอดกลับไปหักจาก BG PAID) รายการเบิกรอรับคืนกลับเป็น PENDING
  // และสลิปถูกถอดออกจาก Bill Images (backend: budget.service.js unreceiveRefundItem)
  const [unreceivingId, setUnreceivingId] = useState(null);
  const handleUnreceive = async (item) => {
    const ok = await confirmAsync(
      `ยกเลิกการคืน "${item.name}" ฿${fmtMoney(item.amount)}?\nรายการจะกลับไปเป็น "รอคืน" และสลิปที่แนบไว้จะถูกเอาออก`,
    );
    if (!ok) return;
    setUnreceivingId(item._id);
    try {
      const updated = await dispatch(actions.budgetUnreceiveRefundItem(budgetId, item._id));
      applyUpdate(updated);
    } catch (error) {
      showAlert('ยกเลิกการคืนไม่สำเร็จ');
    } finally {
      setUnreceivingId(null);
    }
  };

  const openSlipEdit = (item) => {
    setSlipEditItem(item);
    setSlipEditFile(null);
  };

  const closeSlipEdit = () => {
    setSlipEditItem(null);
    setSlipEditFile(null);
  };

  const handleSaveSlipEdit = async () => {
    if (!slipEditItem || !slipEditFile) return;
    setSlipEditSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', slipEditFile, slipEditFile.name);
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/expenses/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (!data?.url) throw new Error('upload failed');
      const updated = await dispatch(
        actions.budgetUpdateRefundSlip(budgetId, slipEditItem._id, { slip_url: data.url, slip_image_id: data.id }),
      );
      applyUpdate(updated);
      closeSlipEdit();
    } catch (error) {
      showAlert('บันทึกหลักฐานใหม่ไม่สำเร็จ');
    } finally {
      setSlipEditSaving(false);
    }
  };

  const handleConfirmReceive = async () => {
    // บังคับแนบสลิปทุกครั้ง (backend receiveRefundItem ก็ปฏิเสธถ้าไม่มี slip_url)
    if (!receiveFile) return;
    setReceiveSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', receiveFile, receiveFile.name);
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/expenses/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (!data?.url) throw new Error('upload failed');
      const updated = await dispatch(
        actions.budgetReceiveRefundItem(budgetId, receiveItem._id, { slip_url: data.url, slip_image_id: data.id }),
      );
      applyUpdate(updated);
      closeReceiveDialog();
    } catch (error) {
      showAlert('บันทึกการรับเงินคืนไม่สำเร็จ');
    } finally {
      setReceiveSaving(false);
    }
  };

  return (
    <>
      <DevWinBadge
        num={151}
        name="RefundItemsModal · รายการเงินที่รอรับคืน"
        open={!!open}
        bottom={84}
      />
      <DevTabBadge tabNum={10} name="รอคืน" active={!!open && refundTab === 0} />
      <DevTabBadge tabNum={11} name="คืนแล้ว" active={!!open && refundTab === 1} />

      {/* Refund pending items modal */}
      <Modal open={!!open} onClose={onClose}>
        <Fade in={!!open}>
          <Box sx={modalStyle}>
            {/* Header + stats */}
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={700} noWrap>
                    รายการเงินที่รอรับคืน
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mt: 0.6,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 1.5,
                      bgcolor: alpha(REFUND_PENDING_COLOR, 0.1),
                      maxWidth: '100%',
                    }}
                  >
                    <Typography noWrap sx={{ fontWeight: 700, color: REFUND_PENDING_COLOR }}>
                      {localBudget?.prefix}{localBudget?.budget_number}
                    </Typography>
                    <Typography noWrap sx={{ fontWeight: 700, color: REFUND_PENDING_COLOR }}>
                      {localBudget?.name}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="เพิ่มรายการรอรับคืน">
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={openAddDialog}
                      sx={{ bgcolor: REFUND_PENDING_COLOR, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#0270ad' } }}
                    >
                      เพิ่มรายการ
                    </Button>
                  </Tooltip>
                  <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.2, mt: 2.5 }}>
                <Box sx={{ flex: 1, borderRadius: 2, p: 1.2, bgcolor: alpha(REFUND_PENDING_COLOR, 0.08), border: '1px solid', borderColor: alpha(REFUND_PENDING_COLOR, 0.3) }}>
                  <Typography variant="caption" sx={{ color: REFUND_PENDING_COLOR, fontWeight: 700, display: 'block' }}>
                    รอคืน ({refundItemsPending.length})
                  </Typography>
                  <Typography fontWeight={700} sx={{ color: REFUND_PENDING_COLOR }}>
                    ฿{fmtMoney(_.sumBy(refundItemsPending, 'amount'))}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, borderRadius: 2, p: 1.2, bgcolor: alpha(REFUND_RECEIVED_COLOR, 0.08), border: '1px solid', borderColor: alpha(REFUND_RECEIVED_COLOR, 0.3) }}>
                  <Typography variant="caption" sx={{ color: REFUND_RECEIVED_COLOR, fontWeight: 700, display: 'block' }}>
                    คืนแล้ว ({refundItemsReceived.length})
                  </Typography>
                  <Typography fontWeight={700} sx={{ color: REFUND_RECEIVED_COLOR }}>
                    ฿{fmtMoney(_.sumBy(refundItemsReceived, 'amount'))}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, borderRadius: 2, p: 1.2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block' }}>
                    รวมทั้งหมด ({refundItemsAll.length})
                  </Typography>
                  <Typography fontWeight={700}>
                    ฿{fmtMoney(_.sumBy(refundItemsAll, 'amount'))}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Tabs
              value={refundTab}
              onChange={(_ev, v) => setRefundTab(v)}
              variant="fullWidth"
              sx={{ minHeight: 42, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, '& .MuiTab-root': { minHeight: 42, fontSize: '0.85rem', fontWeight: 600, py: 0.5 } }}
            >
              <Tab label={`รอคืน (${refundItemsPending.length})`} sx={{ color: refundTab === 0 ? REFUND_PENDING_COLOR : undefined }} />
              <Tab label={`คืนแล้ว (${refundItemsReceived.length})`} sx={{ color: refundTab === 1 ? REFUND_RECEIVED_COLOR : undefined }} />
            </Tabs>

            {/* List */}
            <Box sx={{ overflowY: 'auto', flex: 1, px: 2, py: 1.5, bgcolor: alpha('#000', 0.015) }}>
              {_.isEmpty(refundItemsShown) ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: refundTab === 0 ? 1.5 : 0 }}>
                    {refundTab === 0 ? 'ยังไม่มีรายการรอรับคืน' : 'ยังไม่มีรายการที่ได้รับคืนแล้ว'}
                  </Typography>
                  {refundTab === 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={openAddDialog}
                      sx={{ color: REFUND_PENDING_COLOR, borderColor: REFUND_PENDING_COLOR }}
                    >
                      เพิ่มรายการรอรับคืน
                    </Button>
                  )}
                </Box>
              ) : (
                _.orderBy(refundItemsShown, ['createdAt'], ['desc']).map((item) => {
                  const color = item.refunded ? REFUND_RECEIVED_COLOR : REFUND_PENDING_COLOR;
                  // source_expense_ids (หลายรายการ) — รายการเก่ายังเก็บไว้ที่ source_expense_id (รายการเดียว)
                  const linkedIds = (item.source_expense_ids?.length
                    ? item.source_expense_ids
                    : [item.source_expense_id].filter(Boolean)
                  ).map(String);
                  return (
                    <Box
                      key={item._id}
                      sx={{
                        position: 'relative',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        pl: 2,
                        pr: 1.5,
                        py: 1.25,
                        mb: 1.25,
                        overflow: 'hidden',
                        transition: 'border-color .15s ease, box-shadow .15s ease',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 4,
                          bgcolor: color,
                        },
                        '&:hover': { borderColor: alpha(color, 0.5), boxShadow: `0 3px 12px ${alpha(color, 0.12)}` },
                      }}
                    >
                      {/* Row 1: refund item */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(color, 0.12),
                          }}
                        >
                          {item.refunded
                            ? <CheckCircleIcon sx={{ fontSize: 18, color }} />
                            : <HourglassEmptyIcon sx={{ fontSize: 18, color }} />}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={700} noWrap title={item.name}>{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.refunded
                              ? `ได้รับคืนเมื่อ ${item.refundedAt ? dayjs(item.refundedAt).format('DD/MM/YYYY') : '-'}`
                              : `เพิ่มเมื่อ ${item.createdAt ? dayjs(item.createdAt).format('DD/MM/YYYY') : '-'}`}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0, mr: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                            {item.refunded ? 'ได้รับคืน' : 'รอรับคืน'}
                          </Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            ฿{fmtMoney(item.amount)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                          {item.refunded ? (
                            <>
                              {item.slip_url && (
                                <Tooltip title="ดูหลักฐาน">
                                  <IconButton size="small" onClick={() => window.open(item.slip_url, '_blank')}>
                                    <AttachFileIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title={item.slip_url ? 'แก้ไขหลักฐาน' : 'แนบหลักฐาน'}>
                                <IconButton size="small" onClick={() => openSlipEdit(item)} sx={{ color: REFUND_RECEIVED_COLOR }}>
                                  <EditIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                              {verifiedItemIds.has(String(item._id)) ? (
                                <Tooltip title="ใบรายรับเงินคืนนี้ยืนยัน OCR ในบัญชีบริษัทแล้ว ยกเลิกการคืนหรือลบไม่ได้">
                                  <Chip
                                    size="small"
                                    icon={<VerifiedIcon />}
                                    label="ยืนยัน OCR แล้ว"
                                    sx={{
                                      ml: 0.5,
                                      fontWeight: 700,
                                      color: REFUND_RECEIVED_COLOR,
                                      bgcolor: alpha(REFUND_RECEIVED_COLOR, 0.12),
                                      '& .MuiChip-icon': { color: REFUND_RECEIVED_COLOR },
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  startIcon={unreceivingId === item._id ? <CircularProgress size={14} color="inherit" /> : <UndoIcon fontSize="small" />}
                                  disabled={unreceivingId === item._id}
                                  onClick={() => handleUnreceive(item)}
                                  sx={{ whiteSpace: 'nowrap', borderRadius: 999, ml: 0.5 }}
                                >
                                  ยกเลิกการคืน
                                </Button>
                              )}
                            </>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              disableElevation
                              sx={{ bgcolor: REFUND_PENDING_COLOR, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#0270ad' } }}
                              startIcon={<PaidIcon fontSize="small" />}
                              onClick={() => openReceiveDialog(item)}
                            >
                              ได้รับคืนแล้ว
                            </Button>
                          )}
                          {!item.refunded && (
                            <Tooltip title="แก้ไข">
                              <IconButton size="small" onClick={() => openEditDialog(item)} sx={{ color: REFUND_PENDING_COLOR }}>
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!verifiedItemIds.has(String(item._id)) && (
                            <Tooltip title="ลบรายการ">
                              <IconButton size="small" onClick={() => handleRemoveRefundItem(item._id)} sx={{ color: 'text.disabled' }}>
                                <DeleteIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>

                      {/* Row 2: the expenses this refund belongs to — double-click one to open its expense detail */}
                      {linkedIds.length ? (
                        <Box sx={{ mt: 1, ml: 5.2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            ค่าคืนของรายการเบิก{linkedIds.length > 1 ? ` (${linkedIds.length} รายการ)` : ''}
                          </Typography>
                          <Stack spacing={0.75}>
                            {linkedIds.map((expId) => {
                              const linked = expenseById.get(expId);
                              return (
                                <Tooltip key={expId} title="ดับเบิลคลิกเพื่อดูรายละเอียดรายการเบิก" placement="top-start">
                                  <Box
                                    onDoubleClick={() => setExpenseDetailId(expId)}
                                    sx={{
                                      px: 1.25,
                                      py: 0.9,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.25,
                                      borderRadius: 1.5,
                                      bgcolor: alpha('#64748b', 0.06),
                                      border: '1px solid',
                                      borderColor: alpha('#64748b', 0.15),
                                      cursor: 'pointer',
                                      userSelect: 'none',
                                      transition: 'background-color .15s ease, border-color .15s ease',
                                      '&:hover': { bgcolor: alpha(REFUND_PENDING_COLOR, 0.06), borderColor: alpha(REFUND_PENDING_COLOR, 0.35) },
                                    }}
                                  >
                                    <ReceiptLongOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                                        {linked?.name || 'รายการเบิก (ไม่พบข้อมูลในงบนี้)'}
                                      </Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 0.25, flexWrap: 'wrap' }}>
                                        {linked?.code && (
                                          <Box
                                            component="span"
                                            sx={{
                                              px: 0.75,
                                              borderRadius: 1,
                                              fontSize: '0.7rem',
                                              fontWeight: 700,
                                              fontFamily: 'monospace',
                                              color: REFUND_PENDING_COLOR,
                                              bgcolor: alpha(REFUND_PENDING_COLOR, 0.1),
                                            }}
                                          >
                                            {linked.code}
                                          </Box>
                                        )}
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color: 'text.secondary' }}>
                                          <EventOutlinedIcon sx={{ fontSize: 13 }} />
                                          <Typography variant="caption">
                                            ตั้งเบิก {linked?.date ? dayjs(linked.date).format('DD/MM/YYYY') : '-'}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Box>
                                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                                        ยอดเบิก
                                      </Typography>
                                      <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                        {linked ? `฿${fmtMoney(linked.price)}` : '-'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Tooltip>
                              );
                            })}
                          </Stack>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75, ml: 5.2, fontStyle: 'italic' }}>
                          ไม่ได้ผูกกับรายการเบิก
                        </Typography>
                      )}
                    </Box>
                  );
                })
              )}
            </Box>

          </Box>
        </Fade>
      </Modal>

      {/* Expense detail — same modal as ExpensesTableApplove.js (double-click the linked expense) */}
      <Modal open={!!expenseDetailId} onClose={() => setExpenseDetailId(null)} closeAfterTransition>
        <Fade in={!!expenseDetailId}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '98%', sm: '95%', md: '90%', lg: '82%' },
              maxHeight: '95vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'primary.main',
                color: 'white',
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                รายละเอียดรายการเบิกจ่าย
              </Typography>
              <IconButton size="small" onClick={() => setExpenseDetailId(null)} sx={{ color: 'white' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ overflowY: 'auto', flex: 1, p: { xs: 1, sm: 2 } }}>
              {expenseDetailId && (
                <ExpensesDetail propId={expenseDetailId} onClose={() => setExpenseDetailId(null)} />
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Add refund item dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={closeAddDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: `linear-gradient(135deg, ${alpha(REFUND_PENDING_COLOR, 0.16)} 0%, ${alpha(REFUND_PENDING_COLOR, 0.03)} 100%)`,
            borderBottom: '1px solid',
            borderColor: alpha(REFUND_PENDING_COLOR, 0.15),
          }}
        >
          <Avatar sx={{ width: 42, height: 42, bgcolor: REFUND_PENDING_COLOR, boxShadow: `0 4px 12px ${alpha(REFUND_PENDING_COLOR, 0.35)}` }}>
            <AssignmentReturnIcon />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              เพิ่มรายการรอรับคืน
            </Typography>
            <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
              <Box component="span" sx={{ fontWeight: 700, color: REFUND_PENDING_COLOR, mr: 0.75 }}>
                {localBudget?.prefix}{localBudget?.budget_number}
              </Box>
              {localBudget?.name}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeAddDialog} disabled={refundSaving} sx={{ alignSelf: 'flex-start' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
          {/* Step 1: link to an expense */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            <Box sx={addStepBadgeSx}>1</Box>
            <Typography variant="subtitle2" fontWeight={700}>
              ผูกกับรายการเบิก <Box component="span" sx={{ color: 'error.main' }}>*</Box>
            </Typography>
          </Box>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={expenseOptions}
            ListboxProps={{ style: { maxHeight: 360 } }}
            value={refundForm.selectedExpenses}
            onChange={(_ev, list) => setRefundForm((prev) => ({ ...prev, selectedExpenses: list }))}
            getOptionLabel={expenseLabel}
            isOptionEqualToValue={(a, b) => String(a?._id || a?.id) === String(b?._id || b?.id)}
            // รายการที่เลือกแล้วโชว์เป็นการ์ดด้านล่างแทน chip ในช่องค้นหา (ชื่อยาว chip จะล้นช่อง)
            renderTags={() => null}
            renderOption={(props, exp, { selected }) => (
              <Box component="li" {...props} key={exp?._id || exp?.id} sx={{ display: 'flex', alignItems: 'flex-start !important', gap: 1, py: '6px !important' }}>
                <Checkbox size="small" checked={selected} sx={{ p: 0.25, mt: -0.25 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{exp?.name || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    <Box component="span" sx={{ fontWeight: 700, ...dateGroupStyleByKey.get(expenseDateKey(exp)) }}>
                      {exp?.date ? dayjs(exp.date).format('DD/MM/YYYY') : '-'}
                    </Box>
                    {exp?.code ? ` · ${exp.code}` : ''}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  ฿{fmtMoney(exp?.price)}
                </Typography>
              </Box>
            )}
            noOptionsText="ไม่มีรายการเบิกในงบนี้"
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                size="small"
                placeholder={
                  refundForm.selectedExpenses.length
                    ? `เลือกแล้ว ${refundForm.selectedExpenses.length} รายการ — ค้นหาเพื่อเพิ่ม...`
                    : 'ค้นหาชื่อหรือรหัสรายการเบิก (เลือกได้หลายรายการ)...'
                }
                error={showAddError('expense')}
                helperText={showAddError('expense') ? 'กรุณาเลือกรายการเบิกอย่างน้อย 1 รายการ' : ''}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {refundForm.selectedExpenses.length > 0 && (
            <Box
              sx={{
                mt: 1.25,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: alpha(REFUND_PENDING_COLOR, 0.4),
                bgcolor: alpha(REFUND_PENDING_COLOR, 0.04),
                overflow: 'hidden',
              }}
            >
              <Stack divider={<Divider flexItem sx={{ borderColor: alpha(REFUND_PENDING_COLOR, 0.15) }} />}>
                {refundForm.selectedExpenses.map((exp) => {
                  const expId = String(exp?._id || exp?.id);
                  return (
                    <Box key={expId} sx={{ px: 1.25, py: 1, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar sx={{ width: 30, height: 30, bgcolor: alpha(REFUND_PENDING_COLOR, 0.12), color: REFUND_PENDING_COLOR }}>
                        <ReceiptLongOutlinedIcon sx={{ fontSize: 17 }} />
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>{exp?.name || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {exp?.date ? dayjs(exp.date).format('DD/MM/YYYY') : '-'}
                          {exp?.code ? ` · ${exp.code}` : ''}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        ฿{fmtMoney(exp?.price)}
                      </Typography>
                      <Tooltip title="เอาออก">
                        <IconButton
                          size="small"
                          onClick={() => setRefundForm((prev) => ({
                            ...prev,
                            selectedExpenses: prev.selectedExpenses.filter((e) => String(e?._id || e?.id) !== expId),
                          }))}
                          sx={{ color: 'text.disabled' }}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Stack>
              <Box
                sx={{
                  px: 1.25,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  borderTop: '1px solid',
                  borderColor: alpha(REFUND_PENDING_COLOR, 0.2),
                  bgcolor: alpha(REFUND_PENDING_COLOR, 0.06),
                }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  ยอดเบิกรวม {refundForm.selectedExpenses.length} รายการ
                </Typography>
                <Typography variant="body2" fontWeight={800} sx={{ whiteSpace: 'nowrap', color: REFUND_PENDING_COLOR, fontVariantNumeric: 'tabular-nums' }}>
                  ฿{fmtMoney(selectedExpensesTotal)}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setRefundForm((prev) => ({ ...prev, amount: String(selectedExpensesTotal) }))}
                  sx={{ whiteSpace: 'nowrap', borderRadius: 999, color: REFUND_PENDING_COLOR, borderColor: alpha(REFUND_PENDING_COLOR, 0.5) }}
                >
                  ใช้ยอดเต็ม
                </Button>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 2.5 }} />

          {/* Step 2: refund details */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={addStepBadgeSx}>2</Box>
            <Typography variant="subtitle2" fontWeight={700}>รายละเอียดเงินคืน</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              required
              label="ชื่อรายการ"
              placeholder="เช่น ค่ามัดจำเช่าเครื่องจักร"
              value={refundForm.name}
              onChange={(ev) => setRefundForm((prev) => ({ ...prev, name: ev.target.value }))}
              fullWidth
              error={showAddError('name')}
              helperText={showAddError('name') ? 'กรุณากรอกชื่อรายการ' : ''}
            />
            <TextField
              required
              type="number"
              label="จำนวนเงินที่รอรับคืน"
              value={refundForm.amount}
              onChange={(ev) => setRefundForm((prev) => ({ ...prev, amount: ev.target.value }))}
              fullWidth
              error={showAddError('amount') || addAmountOverExpense}
              helperText={
                showAddError('amount')
                  ? 'กรุณากรอกจำนวนเงินมากกว่า 0'
                  : addAmountOverExpense
                    ? `มากกว่ายอดเบิกรวมของรายการที่เลือก (฿${fmtMoney(selectedExpensesTotal)})`
                    : ' '
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">฿</InputAdornment>,
                sx: { fontSize: '1.15rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              p: 1.25,
              borderRadius: 2,
              bgcolor: alpha(REFUND_PENDING_COLOR, 0.06),
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: REFUND_PENDING_COLOR, mt: 0.1 }} />
            <Typography variant="caption" color="text.secondary">
              ยอดนี้จะถูกหักออกจาก BG PAID ของโครงการระหว่างรอคืน และกลับมานับรวมเมื่อกด &quot;ได้รับคืนแล้ว&quot;
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeAddDialog} disabled={refundSaving} sx={{ color: 'text.secondary' }}>
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            disableElevation
            startIcon={refundSaving ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutlineIcon />}
            sx={{ bgcolor: REFUND_PENDING_COLOR, borderRadius: 999, px: 2.5, '&:hover': { bgcolor: '#0270ad' } }}
            onClick={handleAddRefundItem}
            disabled={refundSaving}
          >
            {refundSaving ? 'กำลังบันทึก...' : 'เพิ่มรายการ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit refund item dialog */}
      <Dialog open={!!editItem} onClose={closeEditDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon sx={{ color: REFUND_PENDING_COLOR }} />
          แก้ไขรายการรอรับคืน
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              autoFocus
              label="ชื่อรายการ"
              value={editForm.name}
              onChange={(ev) => setEditForm((prev) => ({ ...prev, name: ev.target.value }))}
              fullWidth
            />
            <TextField
              type="number"
              label="จำนวนเงิน"
              value={editForm.amount}
              onChange={(ev) => setEditForm((prev) => ({ ...prev, amount: ev.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeEditDialog} disabled={editSaving}>ยกเลิก</Button>
          <Button
            variant="contained"
            disableElevation
            sx={{ bgcolor: REFUND_PENDING_COLOR, '&:hover': { bgcolor: '#0270ad' } }}
            onClick={handleUpdateRefundItem}
            disabled={editSaving}
          >
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receive confirmation dialog */}
      <Dialog
        open={!!receiveItem}
        onClose={() => !receiveSaving && closeReceiveDialog()}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: `linear-gradient(135deg, ${alpha(REFUND_RECEIVED_COLOR, 0.16)} 0%, ${alpha(REFUND_RECEIVED_COLOR, 0.03)} 100%)`,
            borderBottom: '1px solid',
            borderColor: alpha(REFUND_RECEIVED_COLOR, 0.15),
          }}
        >
          <Avatar sx={{ width: 42, height: 42, bgcolor: REFUND_RECEIVED_COLOR, boxShadow: `0 4px 12px ${alpha(REFUND_RECEIVED_COLOR, 0.35)}` }}>
            <PaidIcon />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              ยืนยันได้รับเงินคืน
            </Typography>
            <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
              <Box component="span" sx={{ fontWeight: 700, color: REFUND_RECEIVED_COLOR, mr: 0.75 }}>
                {localBudget?.prefix}{localBudget?.budget_number}
              </Box>
              {localBudget?.name}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeReceiveDialog} disabled={receiveSaving} sx={{ alignSelf: 'flex-start' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
          {/* Summary card */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              textAlign: 'center',
              border: '1px solid',
              borderColor: alpha(REFUND_RECEIVED_COLOR, 0.25),
              bgcolor: alpha(REFUND_RECEIVED_COLOR, 0.05),
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              ยอดเงินที่ได้รับคืน
            </Typography>
            <Typography sx={{ fontSize: '1.9rem', fontWeight: 800, lineHeight: 1.2, color: REFUND_RECEIVED_COLOR, fontVariantNumeric: 'tabular-nums' }}>
              ฿{fmtMoney(receiveItem?.amount)}
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ mt: 0.75, wordBreak: 'break-word' }}>
              {receiveItem?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              เพิ่มเมื่อ {receiveItem?.createdAt ? dayjs(receiveItem.createdAt).format('DD/MM/YYYY') : '-'}
            </Typography>
          </Box>

          {/* Linked expenses */}
          {receiveLinkedExpenses.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                ค่าคืนของรายการเบิก
              </Typography>
              <Stack spacing={0.5}>
                {receiveLinkedExpenses.map(({ id: expId, exp }) => (
                  <Box
                    key={expId}
                    sx={{
                      px: 1.25,
                      py: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      borderRadius: 1.5,
                      bgcolor: alpha('#64748b', 0.06),
                    }}
                  >
                    <ReceiptLongOutlinedIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}>
                        {exp?.name || 'รายการเบิก (ไม่พบข้อมูลในงบนี้)'}
                      </Typography>
                      {exp?.code && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {exp.code}
                        </Typography>
                      )}
                    </Box>
                    {exp && (
                      <Typography variant="caption" fontWeight={700} sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        ฿{fmtMoney(exp.price)}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Slip */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 0.5 }}>
            หลักฐานการรับเงินคืน <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          {receiveFile ? (
            <Box
              sx={{
                p: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {receivePreviewUrl ? (
                <Box
                  component="img"
                  src={receivePreviewUrl}
                  alt=""
                  sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0, bgcolor: 'action.hover' }}
                />
              ) : (
                <AttachFileIcon sx={{ color: 'text.secondary' }} />
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap title={receiveFile.name}>{receiveFile.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {(receiveFile.size / 1024).toFixed(0)} KB
                </Typography>
              </Box>
              <Button component="label" size="small" disabled={receiveSaving} sx={{ whiteSpace: 'nowrap' }}>
                เปลี่ยน
                <input type="file" accept="image/*" hidden onChange={(ev) => setReceiveFile(ev.target.files?.[0] || null)} />
              </Button>
              <Tooltip title="เอาออก">
                <span>
                  <IconButton size="small" onClick={() => setReceiveFile(null)} disabled={receiveSaving}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ) : (
            <Box
              component="label"
              sx={{
                py: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: alpha(REFUND_RECEIVED_COLOR, 0.45),
                color: 'text.secondary',
                cursor: 'pointer',
                transition: 'background-color .15s ease, border-color .15s ease',
                '&:hover': { bgcolor: alpha(REFUND_RECEIVED_COLOR, 0.05), borderColor: REFUND_RECEIVED_COLOR },
              }}
            >
              <CloudUploadOutlinedIcon sx={{ color: REFUND_RECEIVED_COLOR }} />
              <Typography variant="body2" fontWeight={600} sx={{ color: REFUND_RECEIVED_COLOR }}>
                คลิกเพื่อแนบรูปสลิป/หลักฐาน
              </Typography>
              <Typography variant="caption">ต้องแนบก่อนยืนยัน · รองรับไฟล์รูปภาพ</Typography>
              <input type="file" accept="image/*" hidden onChange={(ev) => setReceiveFile(ev.target.files?.[0] || null)} />
            </Box>
          )}

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              p: 1.25,
              borderRadius: 2,
              bgcolor: alpha(REFUND_RECEIVED_COLOR, 0.06),
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: REFUND_RECEIVED_COLOR, mt: 0.1 }} />
            <Typography variant="caption" color="text.secondary">
              ยืนยันแล้วรายการนี้จะย้ายไปแท็บ &quot;คืนแล้ว&quot; และยอดจะกลับมานับรวมใน BG PAID ของโครงการ
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeReceiveDialog} disabled={receiveSaving} sx={{ color: 'text.secondary' }}>
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            disableElevation
            startIcon={receiveSaving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            sx={{ bgcolor: REFUND_RECEIVED_COLOR, borderRadius: 999, px: 2.5, '&:hover': { bgcolor: '#1b5e20' } }}
            onClick={handleConfirmReceive}
            disabled={receiveSaving || !receiveFile}
          >
            {receiveSaving ? 'กำลังบันทึก...' : 'ยืนยันได้รับเงินคืน'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit slip dialog (received items) */}
      <Dialog
        open={!!slipEditItem}
        onClose={() => !slipEditSaving && closeSlipEdit()}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: `linear-gradient(135deg, ${alpha(REFUND_RECEIVED_COLOR, 0.16)} 0%, ${alpha(REFUND_RECEIVED_COLOR, 0.03)} 100%)`,
            borderBottom: '1px solid',
            borderColor: alpha(REFUND_RECEIVED_COLOR, 0.15),
          }}
        >
          <Avatar sx={{ width: 42, height: 42, bgcolor: REFUND_RECEIVED_COLOR, boxShadow: `0 4px 12px ${alpha(REFUND_RECEIVED_COLOR, 0.35)}` }}>
            <AttachFileIcon />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              แก้ไขหลักฐานการรับเงินคืน
            </Typography>
            <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
              {slipEditItem?.name} · ฿{fmtMoney(slipEditItem?.amount)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeSlipEdit} disabled={slipEditSaving} sx={{ alignSelf: 'flex-start' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {/* Current slip */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                หลักฐานเดิม
              </Typography>
              <Box
                sx={{
                  height: 200,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {slipEditItem?.slip_url ? (
                  <Tooltip title="คลิกเพื่อเปิดรูปเต็ม">
                    <Box
                      component="img"
                      src={slipEditItem.slip_url}
                      alt=""
                      onClick={() => window.open(slipEditItem.slip_url, '_blank')}
                      sx={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'zoom-in' }}
                    />
                  </Tooltip>
                ) : (
                  <Typography variant="caption" color="text.disabled">ยังไม่มีหลักฐาน</Typography>
                )}
              </Box>
            </Box>

            {/* New slip */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                หลักฐานใหม่ <Box component="span" sx={{ color: 'error.main' }}>*</Box>
              </Typography>
              {slipEditFile ? (
                <Box
                  sx={{
                    position: 'relative',
                    height: 200,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: REFUND_RECEIVED_COLOR,
                    bgcolor: 'action.hover',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {slipEditPreviewUrl ? (
                    <Box component="img" src={slipEditPreviewUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <Typography variant="caption">{slipEditFile.name}</Typography>
                  )}
                  <Box sx={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 0.5 }}>
                    <Button
                      component="label"
                      size="small"
                      variant="contained"
                      disableElevation
                      disabled={slipEditSaving}
                      sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.72rem', bgcolor: alpha('#000', 0.55), '&:hover': { bgcolor: alpha('#000', 0.7) } }}
                    >
                      เปลี่ยน
                      <input type="file" accept="image/*" hidden onChange={(ev) => setSlipEditFile(ev.target.files?.[0] || null)} />
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => setSlipEditFile(null)}
                      disabled={slipEditSaving}
                      sx={{ p: 0.4, color: '#fff', bgcolor: alpha('#000', 0.55), '&:hover': { bgcolor: alpha('#000', 0.7) } }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box
                  component="label"
                  sx={{
                    height: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: alpha(REFUND_RECEIVED_COLOR, 0.45),
                    color: 'text.secondary',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(REFUND_RECEIVED_COLOR, 0.05), borderColor: REFUND_RECEIVED_COLOR },
                  }}
                >
                  <CloudUploadOutlinedIcon sx={{ color: REFUND_RECEIVED_COLOR }} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: REFUND_RECEIVED_COLOR }}>
                    คลิกเพื่อเลือกรูปใหม่
                  </Typography>
                  <Typography variant="caption">รองรับไฟล์รูปภาพ</Typography>
                  <input type="file" accept="image/*" hidden onChange={(ev) => setSlipEditFile(ev.target.files?.[0] || null)} />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeSlipEdit} disabled={slipEditSaving} sx={{ color: 'text.secondary' }}>
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            disableElevation
            startIcon={slipEditSaving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            sx={{ bgcolor: REFUND_RECEIVED_COLOR, borderRadius: 999, px: 2.5, '&:hover': { bgcolor: '#1b5e20' } }}
            onClick={handleSaveSlipEdit}
            disabled={slipEditSaving || !slipEditFile}
          >
            {slipEditSaving ? 'กำลังบันทึก...' : 'บันทึกหลักฐานใหม่'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
